<template>
  <fieldset class="p-0">
    <div class="bg-white -space-y-px" :class="{ 'rounded-md': border }">
      <label
        v-for="(option, index) in options"
        :key="option.value"
        class="relative p-4 flex gap-4 items-center cursor-pointer mb-0 focus_outline-none"
        :class="[
          size === 'sm' ? 'px-4 py-2' : 'p-4',
          {
            selected: modelValue === option.value,
            border: border,
            [`bg-${highlightColor} border-${highlightBorderColor || highlightColor}`]: modelValue === option.value,
            'border-gray-300': modelValue !== option.value,
            'rounded-tl-md rounded-tr-md': border && index === 0,
            'rounded-bl-md rounded-br-md': border && index === options.length - 1,
          },
        ]"
      >
        <input
          class="appearance-none h-4 w-4 cursor-pointer rounded-full border border-gray-300 focus_ring focus_ring-copper-600 flex-shrink-0"
          :data-testid="`radio-input-${index}`"
          type="radio"
          :name="name"
          :value="option.value"
          :class="{
            'bg-copper-700 border-copper-700': modelValue === option.value,
            'hover_bg-gray-200 hover_border-gray-200': modelValue !== option.value,
          }"
          :aria-labelledby="`${name}-${index}-label`"
          :aria-describedby="`${name}-${index}-description`"
          :checked="modelValue === option.value"
          @change="$emit('update:modelValue', option.value, $event)"
          @click="$emit('update:modelValue', option.value, $event)"
        />
        <slot :item="option.originalItem" :component-id="`${name}-${index}`">
          <div class="flex flex-col">
            <span
              :id="`${name}-${index}-label`"
              class="block text-sm font-medium"
              :class="[modelValue === option.value ? `text-${textHighlightColor}` : 'text-gray-900']"
              >{{ option.name }}
            </span>
            <span
              :id="`${name}-${index}-description`"
              class="block text-sm font-light"
              :class="[modelValue === option.value ? `text-${textHighlightColor}` : 'text-gray-700']"
            >
              {{ option.description }}
            </span>
          </div>
        </slot>
      </label>
    </div>
  </fieldset>
</template>

<script lang="ts">
export interface Option {
  value: string | number
  name: string
  description?: string
  originalItem?: any
}
</script>

<script lang="ts" setup>
import type { PropType } from 'vue'

defineProps({
  name: {
    type: String,
    required: true,
  },
  modelValue: {
    type: [String, Number],
    default: null,
  },
  options: {
    type: Array as PropType<Option[]>,
    default: () => [],
  },
  border: {
    type: Boolean,
    default: true,
  },
  highlightColor: {
    type: String,
    default: 'copper-100',
  },
  textHighlightColor: {
    type: String,
    default: 'gray-900',
  },
  highlightBorderColor: {
    type: String,
    default: 'copper-700',
  },
  size: {
    type: String,
    default: 'md',
  },
})

defineEmits(['update:modelValue'])
</script>

<style lang="stylus" scoped>
[type=radio]:checked,
[type=radio]:hover
  background-image url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3ccircle cx='8' cy='8' r='3'/%3e%3c/svg%3e")

/* Make sure non selected label border doesn't overlap */
label.selected + label
  border-top-color: transparent;
</style>
